import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import PencilIcon from '@/assets/icons/pencil-edit-stabilo.svg';
import { type HiringGoal } from '@/components/pages/edit-targets/types';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { useEditTargetsContext } from '@/contexts/edit-targets-context';
import { formatShortDate } from '@/utils/date-util';

interface DetailItemProps {
  label: string;
  value: string | number;
}

function DetailItem({ label, value }: DetailItemProps) {
  return (
    <div className="w-21 flex flex-col gap-1">
      <Typography variant="caption" className="!text-fx-neutral-400">
        {label}
      </Typography>
      <Typography variant="small2">{value}</Typography>
    </div>
  );
}

interface HiringGoalDetailsProps {
  hiringGoal: HiringGoal;
}

export function HiringGoalDetails({ hiringGoal }: HiringGoalDetailsProps) {
  const intl = useIntl();
  const { hiringGoalUrl } = useEditTargetsContext();
  const {
    openingName,
    startDate,
    goal,
    is_current: isCurrent,
    endDate,
  } = hiringGoal;

  return (
    <>
      <div className="flex flex-col items-start gap-2 self-stretch">
        <Typography variant="body2">{openingName}</Typography>
        {!isCurrent && (
          <Typography variant="small1" className="!text-fx-neutral-600">
            <FormattedMessage defaultMessage="Previous <b>Hiring Goal</b> has ended. Please add a new Hiring goal to enable targets" />
          </Typography>
        )}
      </div>
      <div className="flex flex-col items-start gap-4 self-stretch">
        {!isCurrent && (
          <Typography variant="caption">
            <FormattedMessage defaultMessage="Prev. hiring goal" />
          </Typography>
        )}
        <div className="flex gap-6">
          <div className="flex gap-6">
            <DetailItem
              label={intl.formatMessage({ defaultMessage: 'Hiring Target' })}
              value={intl.formatMessage(
                { defaultMessage: '{goal} hires' },
                { goal },
              )}
            />
            <DetailItem
              label={intl.formatMessage({ defaultMessage: 'Start Date' })}
              value={formatShortDate(startDate)}
            />

            <DetailItem
              label={intl.formatMessage({ defaultMessage: 'End Date' })}
              value={formatShortDate(endDate)}
            />
          </div>
          {isCurrent && (
            <Button variant="secondary" asChild className="ml-auto">
              <Link to={hiringGoalUrl ?? ''} target="_blank">
                <img src={PencilIcon} alt="Edit" />
                <FormattedMessage defaultMessage="Edit hiring goal" />
              </Link>
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
